import { useEffect, useState } from "react";
import { NFeDto, NFeFiltrarDto, STORAGENFE_KEY, updateLocal } from "./NFe.Dto";
import useFilterManager from "src/hook/useFilterManager";
import { MapearErrosServidor, parseDate } from "src/hook/Functions";
import {
  NFeBuscarXML,
  NFeConsultarSituacao,
  NFeEnviar,
  NFeExcluir,
  NFeFiltrar,
  NFeImprimir,
} from "src/services/Fiscal.Service";
import { showToast } from "src/componentes";
import { useNavigate } from "react-router-dom";
import NFeMenuCode from "./NFe.Menu.Code";

const NFeCode = () => {
  const [data, setData] = useState<NFeDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalShowPropriedades, setModalShowPropriedades] = useState(false);
  const [nfe, setNFe] = useState<NFeDto>();
  const navigate = useNavigate();
  const menu = NFeMenuCode({ setLoading, data });

  const useFilter = useFilterManager<NFeFiltrarDto>({
    codigoStatusEnvio: null,
    dataEmissaoInicial: null,
    dataEmissaoFinal: null,
    serie: 0,
  });

  useEffect(() => {
    const controller = new AbortController();

    const loadData = async () => {
      setLoading(true);
      try {
        await filtrar(useFilter.filters);
      } finally {
        setLoading(false);
      }
    };

    const hasValidDateRange =
      (useFilter.filters.dataEmissaoInicial &&
        useFilter.filters.dataEmissaoFinal) ||
      (!useFilter.filters.dataEmissaoInicial &&
        !useFilter.filters.dataEmissaoFinal);

    if (hasValidDateRange) {
      loadData();
    }

    return () => controller.abort();
  }, [useFilter.filters]);

  useEffect(() => {
    const newFilters: NFeFiltrarDto = {
      codigoStatusEnvio:
        useFilter.searchParams.get("codigoStatusEnvio") || null,
      dataEmissaoInicial: parseDate(
        useFilter.searchParams.get("dataEmissaoInicial")
      ),
      dataEmissaoFinal: parseDate(
        useFilter.searchParams.get("dataEmissaoFinal")
      ),
      serie: Number(useFilter.searchParams.get("serie")) || 0,
    };

    useFilter.setFilters((prev) =>
      JSON.stringify(prev) === JSON.stringify(newFilters) ? prev : newFilters
    );
  }, [useFilter.searchParams, useFilter.setFilters]);

  const filtrar = async (input: NFeFiltrarDto) => {
    try {
      const response = await NFeFiltrar(input);

      if (response.data.success) {
        setData(response.data.data);
      } else {
        MapearErrosServidor(response.data.notifications, showToast);
      }
    } catch (error) {
      console.error("Erro ao filtrar:", error);
      showToast({
        message: "Ocorreu erro ao buscar dados",
        title: "Erro",
        variant: "Danger",
      });
    }
  };

  const handleSelectStatus = (eventKey?: string | null) => {
    useFilter.updateFilter(
      "codigoStatusEnvio",
      eventKey === "99" ? null : eventKey
    );
  };

  const handleChangeSerie = (event: React.ChangeEvent<HTMLInputElement>) => {
    useFilter.updateFilter("serie", event.target.value || null);
  };

  const handleSetDates = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    useFilter.setMultipleFilters({
      dataEmissaoInicial: start || null,
      dataEmissaoFinal: end || null,
    });
  };

  // Utilitários de formatação
  const formatDate = (date: any) =>
    date ? new Date(date).toLocaleDateString("pt-BR") : "Selecionar";

  const getDatePickerTitle = () => {
    const { dataEmissaoInicial, dataEmissaoFinal } = useFilter.filters;

    if (!dataEmissaoInicial || !dataEmissaoFinal) {
      return "Emissão";
    }

    return `${formatDate(dataEmissaoInicial)} - ${formatDate(
      dataEmissaoFinal
    )}`;
  };

  const onReturnModalPropriedades = () => {
    setModalShowPropriedades(false);
  };

  const onDeleteModalPropriedades = async (value: NFeDto) => {
    setLoading(true);
    await NFeExcluir(value.id)
      .then((response) => {
        if (response.data.success === true) {
          filtrar(useFilter.filters);
        }
        MapearErrosServidor(response.data.notifications, showToast);
      })
      .catch((error) => {
        console.log(error);
        showToast({
          message: "Ocorreu erro ao excluir NFCe",
          title: "Erro",
          variant: "Danger",
        });
        return undefined;
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onPrintModalPropriedades = async (value: NFeDto) => {
    onReturnModalPropriedades();
    setLoading(true);
    await NFeImprimir(value.id)
      .then((response) => {
        if (response.data instanceof Blob) {
          const pdfBlob = new Blob([response.data], {
            type: "application/pdf",
          });
          const pdfURL = URL.createObjectURL(pdfBlob);
          window.open(pdfURL, "_blank");
        } else {
          MapearErrosServidor(response.data.notifications, showToast);
          return;
        }
      })
      .finally(() => setLoading(false));
  };

  const onFetchStatusModalPropriedades = async (value: NFeDto) => {
    onReturnModalPropriedades();
    setLoading(true);
    await NFeConsultarSituacao(value.id)
      .then((response) => {
        MapearErrosServidor(response.data.notifications, showToast);
      })
      .catch((error) => {
        showToast({
          message: "Ocorreu erro ao consultar situação",
          title: "Erro",
          variant: "Danger",
        });
        return undefined;
      })
      .finally(() => setLoading(false));
  };

  const onSendModalPropriedades = async (value: NFeDto) => {
    onReturnModalPropriedades();
    setLoading(true);
    await NFeEnviar(value.id)
      .then((response) => {
        if (response.data.success === true) {
          filtrar(useFilter.filters);
          updateLocal<NFeDto>(STORAGENFE_KEY, null);
        }
        MapearErrosServidor(response.data.notifications, showToast);
      })
      .catch((error) => {
        showToast({
          message: "Ocorreu erro ao consultar situação",
          title: "Erro",
          variant: "Danger",
        });
        return undefined;
      })
      .finally(() => setLoading(false));
  };

  const onDownloadXml = async (value: NFeDto) => {
    setLoading(true);
    setModalShowPropriedades(false);

    try {
      const nomeArquivo = `NFCe_${value.numeroNF}`;
      const response = await NFeBuscarXML([value.id], nomeArquivo);
      const blob = new Blob([response.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${nomeArquivo}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      showToast({
        message: "Ocorreu erro ao baixar XML",
        title: "Erro",
        variant: "Danger",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClickPropriedades = (value: NFeDto) => () => {
    setModalShowPropriedades(true);
    setNFe(value);
  };

  const onEditModalPropriedades = (value: NFeDto) => {
    setModalShowPropriedades(false);
    updateLocal<NFeDto>(STORAGENFE_KEY, value);
    navigate("/fiscal/nfe/cadastro");
  };

  const onDuplicateModalPropriedades = (value: NFeDto) => {
    setModalShowPropriedades(false);
    value.numeroNF = 0;
    value.id = 0;
    updateLocal<NFeDto>(STORAGENFE_KEY, value);
    navigate("/fiscal/nfe/cadastro");
  };

  const onEventModalPropriedades = (value: NFeDto) => {
    setModalShowPropriedades(false);
    navigate(`/fiscal/nfe/evento?nfeId=${value.id}`);
  };

  return {
    data,
    loading,
    handleChangeSerie,
    handleSelectStatus,
    handleSetDates,
    useFilter,
    getDatePickerTitle,
    modalShowPropriedades,
    nfe,
    onReturnModalPropriedades,
    onDeleteModalPropriedades,
    onPrintModalPropriedades,
    onFetchStatusModalPropriedades,
    onDownloadXml,
    handleClickPropriedades,
    onSendModalPropriedades,
    onEditModalPropriedades,
    onDuplicateModalPropriedades,
    onEventModalPropriedades,
    menu,
  };
};

export default NFeCode;
